'use client'

import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import dayjs from 'dayjs'
import Link from 'next/link'
import * as React from 'react'
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  SubmitHandler,
  useForm,
} from 'react-hook-form'
import toast from 'react-hot-toast'
import {
  mapToObj,
  pick,
} from 'remeda'

import FieldError from '@/components/field-error'
import { useInfoDialog } from '@/components/info-dialog'
import MarkdownEditor from '@/components/markdown-editor'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import {
  Competition,
  useExportApplicantsMutation,
  useExportRankingMutation,
  useImportCompetitorsMutation,
  useReadAllCompetitionsQuery,
  useUpdateOneCompetitionMutation,
} from '@/redux/competition'

interface ExportProps {
  competitionId: number
}

const download = (url: string) => {
  const link = document.createElement('a')
  link.href = url
  document.body.appendChild(link)
  link.click()
  toast.success('Export finished, start to download')
}

function ExportApplicants({ competitionId }: ExportProps) {
  const [exportApplicants, { isLoading }] = useExportApplicantsMutation()
  const onExport = useCallback(async () => {
    if (!competitionId) return
    const { downloadUrl } = await exportApplicants(competitionId).unwrap()
    download(downloadUrl)
  }, [competitionId, exportApplicants])

  return (
    <Button isLoading={isLoading} size="sm" onClick={onExport} disabled={isLoading}>
      Export Applicants
    </Button>
  )
}

function ExportRanking({ competitionId }: ExportProps) {
  const [exportRanking, { isLoading }] = useExportRankingMutation()
  const onExport = useCallback(async () => {
    if (!competitionId) return
    const { downloadUrl } = await exportRanking(competitionId).unwrap()
    download(downloadUrl)
  }, [competitionId, exportRanking])

  return (
    <Button isLoading={isLoading} size="sm" onClick={onExport} disabled={isLoading}>
      Export Ranking
    </Button>
  )
}

interface ImportCompetitorsProps {
  competitionId: number
}

function ImportCompetitors({ competitionId }: ImportCompetitorsProps) {
  const [importCompetitors, { isLoading }] = useImportCompetitorsMutation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const onImportCompetitors = useCallback(async () => {
    fileInputRef.current?.click()
  }, [])
  const info = useInfoDialog()
  const onFileChange = useCallback(async ({ target: { files } }: ChangeEvent<HTMLInputElement>) => {
    const file = files?.[0]
    if (!file || !competitionId) return
    const { total, failureCount, successCount } = await importCompetitors({
      id: competitionId,
      file,
    }).unwrap()
    info({
      title: 'Import Competitors Finished',
      description: `Total: ${total}, Success: ${successCount}, Failure: ${failureCount}`,
    })
  }, [competitionId, importCompetitors, info])

  return (
    <>
      <Button
        onClick={onImportCompetitors}
        disabled={isLoading}
        size="sm"
        isLoading={isLoading}
      >
        Import Competitors
      </Button>
      <VisuallyHidden>
        <input
          type="file"
          ref={fileInputRef}
          onChange={onFileChange}
          accept=".csv, text/csv"
        />
      </VisuallyHidden>
    </>
  )
}

interface EditCompetitionProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  competition?: Competition
}

const inputValueToTimestamp = (value?: string) => {
  if (!value) return undefined
  return dayjs.utc(value).toDate().getTime()
}

const timestampToInputValue = (timestamp?: number) => {
  if (!timestamp) return undefined
  return dayjs(timestamp).utc().format('YYYY-MM-DDTHH:mm')
}

const datetimeFormValues = [
  'applyStartAt',
  'applyEndAt',
  'competitionStartAt',
  'competitionEndAt',
  'rewardAt',
  'publishAt',
] as const

const basicFormValues = [
  'title',
  'subtitle',
  'description',
  'overview',
  'howToParticipate',
  'author',
  'note',
  'applicantLimit',
  'competitorLimit',
] as const

type FormValues = Pick<Competition, typeof basicFormValues[number]> & {
  [K in typeof datetimeFormValues[number]]: string;
};

function EditCompetitionDialog({ competition, open, onOpenChange }: EditCompetitionProps) {
  const [updateCompetition] = useUpdateOneCompetitionMutation()
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>()
  const id = competition?.id
  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    if (id === undefined) return
    await updateCompetition({
      ...pick(values, basicFormValues),
      ...mapToObj(
        datetimeFormValues,
        (key) => [key, inputValueToTimestamp(values[key])],
      ),
      id,
    }).unwrap()
    onOpenChange(false)
    reset()
    toast.success('API connect success.')
  }
  const onClose = useCallback(() => {
    onOpenChange(false)
    reset()
  }, [onOpenChange, reset])

  useEffect(() => {
    if (open && competition !== undefined) {
      reset({
        ...competition,
        ...mapToObj(
          datetimeFormValues,
          (key) => [key, timestampToInputValue(competition[key])],
        ),
      })
    }
  }, [competition, open, reset])

  if (!competition) return

  register('overview', { required: true })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-(--content-width)" showClose={false}>
        <DialogTitle>Edit {competition.title}</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="py-8 h-[calc(100vh-200px)] overflow-y-auto grid gap-4">
            <div>
              <Label required className="mb-3" htmlFor="title">Title</Label>
              <Input
                {...register('title', { required: true })}
                id="title"
                aria-invalid={!!errors.title}
              />
              <FieldError error={errors.title} />
            </div>
            <div>
              <Label className="mb-3" htmlFor="subtitle">Subtitle</Label>
              <Input
                {...register('subtitle')}
                id="subtitle"
                aria-invalid={!!errors.subtitle}
              />
              <FieldError error={errors.subtitle} />
            </div>
            <div className="grid gap-8 grid-cols-2">
              <div>
                <Label required className="mb-3" htmlFor="applyStartAt">Apply Start (UTC)</Label>
                <Input
                  {...register('applyStartAt', { required: true })}
                  id="applyStartAt"
                  type="datetime-local"
                  aria-invalid={!!errors.applyStartAt}
                />
                <FieldError error={errors.applyStartAt} />
              </div>
              <div>
                <Label required className="mb-3" htmlFor="applyEndAt">Apply End (UTC)</Label>
                <Input
                  {...register('applyEndAt', { required: true })}
                  id="applyEndAt"
                  type="datetime-local"
                  aria-invalid={!!errors.applyEndAt}
                />
                <FieldError error={errors.applyEndAt} />
              </div>
              <div>
                <Label required className="mb-3" htmlFor="competitionStartAt">
                  Competition Start (UTC)
                </Label>
                <Input
                  {...register('competitionStartAt', { required: true })}
                  id="competitionStartAt"
                  type="datetime-local"
                  aria-invalid={!!errors.competitionStartAt}
                />
                <FieldError error={errors.competitionStartAt} />
              </div>
              <div>
                <Label required className="mb-3" htmlFor="competitionEndAt">
                  Competition End (UTC)
                </Label>
                <Input
                  {...register('competitionEndAt', { required: true })}
                  id="competitionEndAt"
                  type="datetime-local"
                  aria-invalid={!!errors.competitionEndAt}
                />
                <FieldError error={errors.competitionEndAt} />
              </div>
              <div>
                <Label required className="mb-3" htmlFor="rewardAt">
                  Reward At (UTC)
                </Label>
                <Input
                  {...register('rewardAt', { required: true })}
                  id="rewardAt"
                  type="datetime-local"
                  aria-invalid={!!errors.rewardAt}
                />
                <FieldError error={errors.rewardAt} />
              </div>
              <div>
                <Label required className="mb-3" htmlFor="publishAt">
                  Publish At (UTC)
                </Label>
                <Input
                  {...register('publishAt', { required: true })}
                  id="publishAt"
                  type="datetime-local"
                  aria-invalid={!!errors.publishAt}
                />
                <FieldError error={errors.publishAt} />
              </div>
              <div>
                <Label required className="mb-3" htmlFor="applicantLimit">Applicant Limit</Label>
                <Input
                  {...register('applicantLimit', { required: true })}
                  id="applicantLimit"
                  type="number"
                  aria-invalid={!!errors.applicantLimit}
                />
                <FieldError error={errors.applicantLimit} />
              </div>
              <div>
                <Label required className="mb-3" htmlFor="competitorLimit">Competitor Limit</Label>
                <Input
                  {...register('competitorLimit', { required: true })}
                  id="competitorLimit"
                  type="number"
                  aria-invalid={!!errors.competitorLimit}
                />
                <FieldError error={errors.competitorLimit} />
              </div>
            </div>
            <div>
              <Label required className="mb-3" htmlFor="description">Description</Label>
              <Textarea
                {...register('description', { required: true })}
                id="description"
                aria-invalid={!!errors.description}
                className="min-h-20"
              />
              <FieldError error={errors.description} />
            </div>
            <div>
              <Label required className="mb-3" htmlFor="note">Note</Label>
              <Textarea
                {...register('note', { required: true })}
                id="note"
                aria-invalid={!!errors.note}
                className="min-h-20"
              />
              <FieldError error={errors.note} />
            </div>
            <div>
              <Label required className="mb-3">Overview</Label>
              <MarkdownEditor
                onChange={(markdown) => setValue('overview', markdown)}
                markdown={competition.overview}
              />
              <FieldError error={errors.overview} />
            </div>
            <div>
              <Label required className="mb-3">How to Participate</Label>
              <MarkdownEditor
                onChange={(markdown) => setValue('howToParticipate', markdown)}
                markdown={competition.howToParticipate}
              />
              <FieldError error={errors.howToParticipate} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button isLoading={isSubmitting} type="submit" disabled={isSubmitting}>
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default function Admin() {
  const { data: allCompetitions } = useReadAllCompetitionsQuery()
  const [editingCompetitionId, setEditingCompetitionId] = useState<number>()
  const [updateCompetition] = useUpdateOneCompetitionMutation()

  return (
    <div>
      <div className="text-lg mb-2">
        Competitions
      </div>
      <Table className="mt-4 border-spacing-0">
        <TableHeader>
          <TableRow className="text-sm text-secondary-foreground">
            <TableHead>ID</TableHead>
            <TableHead>Competition</TableHead>
            <TableHead>Applicants</TableHead>
            <TableHead>Operations</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {allCompetitions?.map(({ id, title, applicantCount, isBetting }) => (
            <TableRow key={id} className="bg-background-secondary">
              <TableCell>{id}</TableCell>
              <TableCell>
                <Link target="_blank" href={`/competition/${id}`}>{title}</Link>
              </TableCell>
              <TableCell>{applicantCount}</TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => setEditingCompetitionId(id)}>Edit</Button>
                  <div className="flex flex-col justify-center items-center border rounded-md">
                    <div className="text-sm text-secondary-foreground">Betting</div>
                    <Switch checked={isBetting} onCheckedChange={async (checked) => {
                      await updateCompetition({
                        id,
                        isBetting: checked,
                      }).unwrap()
                    }} />
                  </div>
                  <ExportApplicants competitionId={id} />
                  <ExportRanking competitionId={id} />
                  <ImportCompetitors competitionId={id} />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <EditCompetitionDialog
        open={editingCompetitionId !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setEditingCompetitionId(undefined)
          }
        }}
        competition={allCompetitions?.find(({ id }) => id === editingCompetitionId)}
      />
    </div>
  )
}
